
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { getShareText } from "@/lib/share-utils";
import { useState, useEffect } from "react";
import { getTimeUntilNextPuzzle } from "@/utils/dateUtils";
import { TodaysResultModal } from "./TodaysResultModal";
import type { LogicRule } from "@/lib/game-utils";
import { supabase } from "@/integrations/supabase/client";

interface ResultActionsProps {
  result: "perfect" | "wrong";
  solveTime: number | null;
  onRetry?: () => void;
  isUsingExternalData?: boolean;
  selectedWords?: Array<{ word: any; order: number }>;
  targetOrder?: any[];
  selectedRule: LogicRule;
}

export function ResultActions({ 
  result, 
  solveTime, 
  onRetry,
  isUsingExternalData = false,
  selectedWords = [],
  targetOrder,
  selectedRule
}: ResultActionsProps) {
  
  const { toast } = useToast();
  const [nextPuzzleTime, setNextPuzzleTime] = useState<string>("");
  const [showTodaysModal, setShowTodaysModal] = useState(false);
  const [attemptId, setAttemptId] = useState<string | null>(null);
  
  useEffect(() => {
    const updateCountdown = () => {
      const { hours, minutes } = getTimeUntilNextPuzzle();
      
      if (hours === 0 && minutes === 0) {
        setNextPuzzleTime("Available now!");
        return;
      }
      
      setNextPuzzleTime(`${hours}h ${minutes}m`);
      
      // Log for debugging
      console.log("Result countdown update:", { hours, minutes });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000); // Update every minute
    
    return () => clearInterval(interval);
  }, []);
  
  // Get latest attempt ID for sharing
  useEffect(() => {
    const fetchAttemptId = async () => {
      if (!selectedWords || selectedWords.length === 0) return;
      
      try {
        const userEmail = localStorage.getItem("user_email");
        if (!userEmail) return;
        
        // Get the most recent attempt for this user
        const { data, error } = await supabase
          .from('game_attempts')
          .select('id')
          .eq('email', userEmail)
          .eq('result', result)
          .order('created_at', { ascending: false })
          .limit(1);
        
        if (!error && data && data.length > 0) {
          console.log("Found attempt ID for sharing:", data[0].id);
          setAttemptId(data[0].id);
        }
      } catch (err) {
        console.error("Error fetching attempt ID:", err);
      }
    };
    
    fetchAttemptId();
  }, [selectedWords, result]);

  const handleShare = async () => {
    // Log data for debugging
    console.log("Share data:", { 
      result, 
      solveTime,
      selectedWords, 
      targetOrder,
      attemptId,
      hasWordData: !!(selectedWords && selectedWords.length && targetOrder && targetOrder.length)
    });

    const resultText = getShareText(
      result, 
      solveTime, 
      undefined, 
      selectedWords, 
      targetOrder,
      attemptId || undefined // Pass the attempt ID to getShareText
    );

    try {
      if (navigator.share) {
        await navigator.share({ 
          text: resultText,
        });
      } else {
        await copyToClipboard(resultText);
      }
    } catch (err) {
      console.error("Share error:", err);
      if (err instanceof Error && err.name !== "AbortError") {
        copyToClipboard(resultText);
      }
    }
  };

  const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text);
    toast({
      title: "Copied to clipboard!",
      description: "Share your results with friends"
    });
  };

  return (
    <div className="flex flex-col gap-4 items-center mt-4 px-1">
      <div className="text-sm text-gray-800 font-medium">
        Who else should play?
      </div>
      
      <div className="flex justify-center w-full">
        <Button
          onClick={handleShare}
          className="bg-black text-white hover:bg-gray-800 rounded-full py-2 px-8 transition-all duration-200 font-medium"
        >
          Share Results
        </Button>
      </div>
      
      <div className="flex flex-col gap-2 items-center">
        <Button
          variant="outline"
          className="flex items-center gap-1.5 text-gray-700 hover:text-gray-900 font-medium px-3 py-1.5"
          onClick={() => setShowTodaysModal(true)}
        >
          <span>Today's Player Card</span>
        </Button>
      </div>
      
      <div className="text-xs text-gray-500 mt-1">
        Next puzzle available in {nextPuzzleTime}
        <span className="block text-xs text-gray-400 mt-0.5">
          (Puzzle resets at midnight Eastern Time)
        </span>
      </div>

      <TodaysResultModal
        open={showTodaysModal}
        onOpenChange={setShowTodaysModal}
        selectedWords={selectedWords}
        selectedRule={selectedRule}
      />
    </div>
  );
}
